"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardBody,
  Text,
  Badge,
  HStack,
  VStack,
  Flex,
  Select,
  Tabs,
  TabList,
  Tab,
  Stat,
  StatLabel,
  StatNumber,
  useToast,
  Spinner,
  Avatar,
  Icon,
  IconButton,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { FaWhatsapp } from "react-icons/fa";
import { motion } from "framer-motion";
import WhatsAppPhoneNumbers from "./whatsapp-agents/WhatsAppPhoneNumbers";
import WhatsAppKnowledgeBase from "./whatsapp-agents/WhatsAppKnowledgeBase";
import { CreateAgent } from "./whatsapp-agents/CreateAgent";
import {
  whatsappAgentService,
  type WhatsAppAgent,
} from "../whatsapp-services/agentService";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot, faPhone } from "@fortawesome/free-solid-svg-icons";
import { useStableCredits } from "../hooks/useStableCredits";

const MotionBox = motion(Box);

const WhatsAppAgents: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedAgentType, setSelectedAgentType] =
    React.useState<string>("WhatsApp");
  const [selectedWhatsAppTab, setSelectedWhatsAppTab] = React.useState<string>(
    () => {
      return localStorage.getItem("currentAgentTab") || "agents";
    },
  );

  const [whatsappAgents, setWhatsappAgents] = useState<WhatsAppAgent[]>([]);
  const [stats, setStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
    totalInteractions: 0,
    avgConvLength: 0,
  });

  // Keeping your current behavior: whatsappPhoneNumbers is currently set to []
  const [whatsappPhoneNumbers, setWhatsappPhoneNumbers] = useState<any[]>([]);

  // ✅ Dynamic granular loading (same pattern as your new VoiceAgents page)
  const [loading, setLoading] = React.useState({
    agents: true,
    phoneNumbers: true,
    metrics: true,
  });

  const toast = useToast();
  const { isAdmin, currentClientId, clients, userProfile } = useAuth();
  const credits = useStableCredits();

  const abortControllerRef = React.useRef<AbortController | null>(null);

  // ---- tenant/company resolution (same idea as VoiceAgents) ----
  const companyName = useMemo(() => {
    const currentClient = clients.find((c) => c.id === currentClientId);
    return isAdmin && currentClient
      ? currentClient.company_name
      : userProfile?.company_name;
  }, [isAdmin, currentClientId, clients, userProfile]);

  // Keep selected agent type in sync with route
  React.useEffect(() => {
    if (location.pathname.startsWith("/whatsapp-agents")) {
      setSelectedAgentType("WhatsApp");
      localStorage.setItem("currentAgentType", "WhatsApp");
    }
  }, [location.pathname]);

  // ---- phone numbers (kept behavior: currently hardcoded to []) ----
  const loadWhatsAppPhoneNumbers = useCallback(async () => {
    // NOTE: Keeping your current behavior (set to []) exactly.
    setWhatsappPhoneNumbers([]);
  }, []);

  // ---- stats fetch (tenant-scoped, abortable) ----
  const loadStats = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const url = new URL(
          `${process.env.REACT_APP_SERVER_URL}/api/whatsapp-agents/stats`,
        );
        if (companyName) {
          url.searchParams.append("company_name", companyName);
        }

        const response = await fetch(url.toString(), {
          headers: {
            // Keep existing header behavior
            Authorization: `Bearer ${currentClientId}`,
          },
          signal,
        });

        if (response.ok) {
          const data = await response.json();
          if (!signal?.aborted) setStats(data);
        }
      } catch (error: any) {
        if (error?.name === "AbortError") return;
        console.error("Error loading WhatsApp agent stats:", error);
      }
    },
    [companyName, currentClientId],
  );

  // ---- main loader: NO caching; dynamic loading; no full-screen gate ----
  const fetchData = useCallback(async () => {
    // Abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // ✅ Do NOT blank UI; show tiny spinners while revalidating
    setLoading((prev) => ({
      ...prev,
      agents: true,
      phoneNumbers: true,
      metrics: true,
    }));

    try {
      // Agents (progressive)
      void whatsappAgentService
        .list(companyName)
        .then((freshAgents) => {
          if (signal.aborted) return;

          // Dedupe agents by agent_id (same safety as VoiceAgents)
          const agentMap = new Map<string, WhatsAppAgent>();
          (freshAgents || []).forEach((agent: WhatsAppAgent) => {
            agentMap.set(agent.agent_id, agent);
          });
          const uniqueAgents = Array.from(agentMap.values());

          setWhatsappAgents(uniqueAgents);
        })
        .catch((error: any) => {
          if (error?.name === "AbortError") return;
          console.error("Error loading WhatsApp agents:", error);
        })
        .finally(() => {
          if (!signal.aborted) {
            setLoading((prev) => ({ ...prev, agents: false }));
          }
        });

      // Phone numbers (kept behavior, progressive)
      void (async () => {
        try {
          await loadWhatsAppPhoneNumbers();
        } catch (error: any) {
          if (error?.name === "AbortError") return;
          console.error("Error loading WhatsApp phone numbers:", error);
        } finally {
          if (!signal.aborted) {
            setLoading((prev) => ({ ...prev, phoneNumbers: false }));
          }
        }
      })();

      // Metrics (progressive)
      void (async () => {
        try {
          await loadStats(signal);
        } finally {
          if (!signal.aborted) {
            setLoading((prev) => ({ ...prev, metrics: false }));
          }
        }
      })();
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      console.error("Error loading WhatsApp agents:", error);
      toast({
        title: "Error loading WhatsApp agents",
        status: "error",
        duration: 3000,
      });
      if (!signal.aborted) {
        setLoading({
          agents: false,
          phoneNumbers: false,
          metrics: false,
        });
      }
    }
  }, [companyName, loadWhatsAppPhoneNumbers, loadStats, toast]);

  // initial fetch + cleanup abort
  useEffect(() => {
    fetchData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  // Keep your “refresh stats after loading” behavior (fire-and-forget)
  useEffect(() => {
    void loadStats();
  }, [isAdmin, currentClientId]); // keeping your dependency pattern

  const handleAgentTypeChange = (type: string) => {
    setSelectedAgentType(type);
    localStorage.setItem("currentAgentType", type);

    if (type === "All") {
      navigate("/all-agents");
    } else if (type === "Voice") {
      navigate("/voice-agents");
    } else if (type === "SMS") {
      navigate("/sms-agents");
    } else if (type === "Email") {
      navigate("/email-agents");
    } else if (type === "WhatsApp") {
      navigate("/whatsapp-agents");
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    try {
      await whatsappAgentService.delete(agentId);
      toast({
        title: "WhatsApp Agent deleted successfully",
        status: "success",
        duration: 3000,
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error deleting WhatsApp agent",
        status: "error",
        duration: 3000,
      });
    }
  };

  const getTabIndex = () => {
    switch (selectedWhatsAppTab) {
      case "agents":
        return 0;
      case "phone-numbers":
        return 1;
      case "knowledge-base":
        return 2;
      default:
        return 0;
    }
  };

  const handleTabIndexChange = (index: number) => {
    const tabs = ["agents", "phone-numbers", "knowledge-base"];
    const newTab = tabs[index];
    setSelectedWhatsAppTab(newTab);
    localStorage.setItem("currentAgentTab", newTab);
    window.dispatchEvent(new CustomEvent("agentTabChange", { detail: newTab }));
  };

  if (selectedWhatsAppTab === "phone-numbers") {
    return <WhatsAppPhoneNumbers />;
  }

  if (selectedWhatsAppTab === "knowledge-base") {
    return <WhatsAppKnowledgeBase />;
  }

  // ✅ Removed full-screen loading gate (render shell immediately)
  const disabled = credits !== null && credits < 0.01;

  return (
    <Box p={6} bg="white" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Tabs
            index={getTabIndex()}
            onChange={handleTabIndexChange}
            variant="solid-rounded"
            colorScheme="blackAlpha"
          >
            <TabList
              bg="gray.100"
              p={1}
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.300"
            >
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg: selectedWhatsAppTab === "agents" ? "white" : "gray.200",
                }}
              >
                💬 WhatsApp Agents
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg:
                    selectedWhatsAppTab === "phone-numbers"
                      ? "white"
                      : "gray.200",
                }}
              >
                📞 WhatsApp Phone Numbers
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg:
                    selectedWhatsAppTab === "knowledge-base"
                      ? "white"
                      : "gray.200",
                }}
              >
                📚 Knowledge Base
              </Tab>
            </TabList>
          </Tabs>
        </HStack>

        <HStack spacing={2}>
          <MotionBox
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            transition="0.15s ease"
          >
            <Select
              value={selectedAgentType}
              onChange={(e) => handleAgentTypeChange(e.target.value)}
              height="48px"
              bg="white"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="12px"
              color="gray.800"
              fontWeight="semibold"
              fontSize="sm"
              pl="12px"
              pr="36px"
              cursor="pointer"
              shadow="sm"
              transition="all 0.2s ease"
              _hover={{
                borderColor: "green.400",
                shadow: "md",
              }}
              _focus={{
                borderColor: "green.600",
                boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
              }}
              isDisabled={disabled}
              opacity={disabled ? 0.6 : 1}
              title={
                disabled
                  ? "Insufficient balance. Please add balance to view agents."
                  : undefined
              }
            >
              <option value="All">🤖 All Agents</option>
              <option value="Voice">📞 Voice Agents</option>
              <option value="SMS">💬 SMS Agents</option>

              {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                <option value="Email">📧 Email Agents</option>
              )}

              <option value="WhatsApp">📱 WhatsApp Agents</option>
            </Select>
          </MotionBox>

          <CreateAgent
            onAgentCreated={fetchData}
            buttonProps={{ variant: "brandGradient", isDisabled: disabled }}
          />
        </HStack>
      </Flex>

      {whatsappAgents.length > 0 && (
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
          gap={4}
          mb={6}
        >
          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Total Agents</StatLabel>
                <StatNumber color="black">
                  {loading.metrics ? <Spinner size="sm" /> : stats.totalAgents}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Active Agents</StatLabel>
                <StatNumber color="green.500">
                  {loading.metrics ? <Spinner size="sm" /> : stats.activeAgents}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Total Interactions</StatLabel>
                <StatNumber color="black">
                  {loading.metrics ? (
                    <Spinner size="sm" />
                  ) : (
                    stats.totalInteractions
                  )}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </Grid>
      )}

      {/* Empty state */}
      {whatsappAgents.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="500px"
          minH="calc(100vh - 80px)"
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          {loading.agents ? (
            <Spinner size="xl" color="green.500" />
          ) : (
            <>
              <FontAwesomeIcon icon={faRobot} color="gray" size="3x" />
              <Text fontSize="lg" fontWeight="medium" color="gray.600" mt={4}>
                No WhatsApp Agents Found!
              </Text>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Create a WhatsApp agent to get started.
              </Text>
              <Box mt={6}>
                <CreateAgent
                  onAgentCreated={fetchData}
                  buttonProps={{
                    mt: 6,
                    colorScheme: "green",
                    bg: "green.800",
                    _hover: { bg: "green.900" },
                    isDisabled: disabled,
                  }}
                />
              </Box>
            </>
          )}
        </Flex>
      ) : (
        // ✅ Updated card UI to match your new standard (and removed extra per-agent calculations)
        <Grid templateColumns="repeat(auto-fit, minmax(520px, 1fr))" gap={6}>
          {whatsappAgents.map((agent) => {
            // phone object shape may vary; we keep your existing source (whatsappPhoneNumbers)
            const phoneObj =
              whatsappPhoneNumbers.find(
                (p: any) => p.agent_id === agent.agent_id,
              ) || null;

            const phonePretty =
              phoneObj?.phone_number_pretty ||
              phoneObj?.phone_number ||
              phoneObj?.number ||
              null;

            const hasPhone = !!phonePretty;

            const agentTypeLabel = "Single-Prompt";
            const agentTypeColor = "purple";

            const cardDisabled = disabled;

            return (
              <Card
                role="button"
                tabIndex={cardDisabled ? -1 : 0}
                aria-disabled={cardDisabled}
                key={agent.agent_id}
                borderLeft="4px"
                borderLeftColor="green.500"
                cursor={cardDisabled ? "not-allowed" : "pointer"}
                opacity={cardDisabled ? 0.55 : 1}
                onClick={() => {
                  if (cardDisabled) return;
                  navigate(`/whatsapp-agents/${agent.agent_id}`);
                }}
                _hover={cardDisabled ? {} : { shadow: "md" }}
                transition="all 0.2s"
                minH="250px"
                display="flex"
                flexDirection="column"
                title={
                  cardDisabled
                    ? "Insufficient balance to view agent details"
                    : undefined
                }
              >
                <CardBody display="flex" flexDirection="column" p={5}>
                  <VStack align="stretch" spacing={3} flex={1}>
                    {/* Header */}
                    <Flex justify="space-between" align="start" gap={3}>
                      <HStack align="start" spacing={3} flex={1} minW={0}>
                        {/* Avatar (bigger + green outline) */}
                        <Box
                          w="52px"
                          h="52px"
                          borderRadius="full"
                          border="2px solid"
                          borderColor="green.500"
                          overflow="hidden"
                          flexShrink={0}
                          bg="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Avatar
                            size="sm"
                            bg="green.500"
                            icon={
                              <Icon
                                as={FaWhatsapp}
                                color="white"
                                fontSize="1.05rem"
                              />
                            }
                          />
                        </Box>

                        <VStack align="start" spacing={1} flex={1} minW={0}>
                          <Text
                            fontSize="lg"
                            fontWeight="600"
                            color="black"
                            noOfLines={2}
                            lineHeight="1.2"
                          >
                            {agent.name || "Unnamed Agent"}
                          </Text>

                          <HStack spacing={2} flexWrap="wrap">
                            <Badge colorScheme="green" fontSize="xs">
                              WHATSAPP
                            </Badge>

                            <Badge
                              colorScheme={hasPhone ? "green" : "yellow"}
                              fontSize="xs"
                            >
                              {hasPhone ? "ACTIVE" : "PAUSED"}
                            </Badge>
                          </HStack>
                        </VStack>
                      </HStack>

                      {/* Actions */}
                      <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          aria-label="Edit agent"
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            navigate(`/whatsapp-agents/${agent.agent_id}`)
                          }
                          isDisabled={cardDisabled}
                          title={
                            cardDisabled
                              ? "Insufficient balance to edit agent"
                              : "Edit"
                          }
                        />
                        <IconButton
                          aria-label="Delete agent"
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteAgent(agent.agent_id)}
                          title="Delete"
                        />
                      </HStack>
                    </Flex>

                    {/* Details (same structure as your new standard) */}
                    <VStack
                      align="stretch"
                      spacing={2}
                      pt={2}
                      borderTop="1px solid"
                      borderColor="gray.100"
                    >
                      <Flex justify="space-between" align="center">
                        <Text fontSize="sm" color="gray.600">
                          Phone Number:
                        </Text>

                        {loading.phoneNumbers ? (
                          <Spinner size="xs" />
                        ) : hasPhone ? (
                          <Badge
                            colorScheme="green"
                            fontSize="sm"
                            display="inline-flex"
                            alignItems="center"
                            gap={2}
                            px={2}
                            py={1}
                            borderRadius="md"
                          >
                            <FontAwesomeIcon icon={faPhone} />
                            <Text as="span">{phonePretty}</Text>
                          </Badge>
                        ) : (
                          <Text fontSize="sm" fontWeight="600" color="gray.800">
                            Not connected
                          </Text>
                        )}
                      </Flex>

                      <Flex justify="space-between" align="center">
                        <Text fontSize="sm" color="gray.600">
                          Agent Type:
                        </Text>

                        <Badge colorScheme={agentTypeColor} fontSize="xs">
                          {agentTypeLabel}
                        </Badge>
                      </Flex>
                    </VStack>

                    {/* Footer (Last updated bottom-right) */}
                    <Flex
                      pt={2}
                      mt="auto"
                      borderTop="1px solid"
                      borderColor="gray.100"
                      justify="flex-end"
                    >
                      <Text fontSize="xs" color="gray.500">
                        Last Updated:{" "}
                        {new Date(agent.updated_at).toLocaleString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        })}
                      </Text>
                    </Flex>
                  </VStack>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default WhatsAppAgents;
